import React, { useEffect, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Alert from "@material-ui/lab/Alert";
import InfoIcon from "@material-ui/icons/Info";
import MessageIcon from "@material-ui/icons/Message";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CodeIcon from "@material-ui/icons/Code";
import SettingsInputAntennaIcon from "@material-ui/icons/SettingsInputAntenna";
import GroupIcon from "@material-ui/icons/Group";
import VpnKeyIcon from "@material-ui/icons/VpnKey";

import { i18n } from "../../translate/i18n";
import { Typography } from "@material-ui/core";
import { toast } from "react-toastify";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getAccountApiKey, regenerateAccountApiKey } from "../../services/publicApi.api";

import { useStyles } from "./messagesAPIStyles";
import EndpointCard, { CodeBlock } from "./components/EndpointCard";
import ExternalApiReference from "./components/ExternalApiReference";

const ACCOUNT_BADGE = { label: "TOKEN CUENTA", color: "#7c3aed" };

const BAILEYS_BADGE = { label: "BAILEYS", color: "#25D366" };

const MessagesAPI = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const companyId = user?.companyId;

  const { getPlanCompany } = usePlans();
  const [accountKey, setAccountKey] = useState("");

  useEffect(() => {
    getAccountApiKey()
      .then(({ data }) => setAccountKey(data?.data?.apiToken || ""))
      .catch(() => {});
  }, []);

  const handleRegenerateKey = async () => {
    try {
      const { data } = await regenerateAccountApiKey();
      setAccountKey(data?.data?.apiToken || "");
      toast.success(i18n.t("messagesAPI.account.regenerated"));
    } catch (err) {
      toast.error(i18n.t("messagesAPI.account.regenerateError"));
    }
  };

  useEffect(() => {
    async function fetchData() {
      if (companyId) {
        const planConfigs = await getPlanCompany(undefined, companyId);
        if (!planConfigs.plan.useExternalApi) {
          toast.error(i18n.t("messagesAPI.messages.noPermission"));
          setTimeout(() => {
            history.push(`/`);
          }, 1000);
        }
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBackendUrl = () =>
    // En Docker la URL llega en runtime via /config.js; en bare-metal viene
    // horneada en el build. El fallback a localhost solo aplica en desarrollo.
    window.WATOOLX_BACKEND_URL ||
    (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL
      ? process.env.REACT_APP_BACKEND_URL
      : window.location.origin);

  const backend = getBackendUrl();
  const sendEp = `${backend}/api/messages/send`;
  const groupEp = `${backend}/api/messages/send-group`;
  const externalApiBase = `${backend}/api/external`;
  const instanceBase = `${backend}/api/v1/instance`;
  const instancesEp = `${backend}/api/v1/instances`;

  const nav = [
    { id: "auth", label: i18n.t("messagesAPI.nav.auth") },
    { id: "instance", label: i18n.t("messagesAPI.nav.instance") },
    { id: "messages", label: i18n.t("messagesAPI.nav.messages") },
    { id: "groups", label: i18n.t("messagesAPI.nav.groups") },
    { id: "external", label: i18n.t("messagesAPI.nav.external") },
    { id: "account", label: i18n.t("messagesAPI.nav.account") },
  ];

  return (
    <Paper className={classes.mainPaper} variant="outlined">
      <Typography variant="h4" className={classes.mainHeader}>
        <CodeIcon style={{ marginRight: 16, verticalAlign: "middle" }} />
        {i18n.t("messagesAPI.title")}
      </Typography>

      <Box display="flex" alignItems="center" style={{ marginBottom: 8, gap: 10, flexWrap: "wrap" }}>
        <Chip label="BAILEYS" size="small" style={{ fontWeight: 700, background: "#25D366", color: "#fff" }} />
        <Chip label="NO WABA" size="small" style={{ fontWeight: 700, background: "#f44336", color: "#fff" }} />
        <Typography variant="body2" color="textSecondary">
          {i18n.t("messagesAPI.subtitle")}
        </Typography>
      </Box>

      <Alert severity="warning" className={classes.elementMargin} style={{ marginBottom: 16 }}>
        {i18n.t("messagesAPI.subtitleBody")}
      </Alert>

      <Box className={classes.layout}>
        {/* Sidebar de recursos */}
        <Box className={classes.sidebar}>
          <Box className={classes.sidebarInner}>
            {nav.map((n) => (
              <a key={n.id} href={`#${n.id}`} className={classes.navItem}>
                {n.label}
              </a>
            ))}
          </Box>
        </Box>

        {/* Contenido */}
        <Box className={classes.content}>
          {/* Autenticación */}
          <Box id="auth" className={classes.authCard}>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 700 }}>
              <InfoIcon style={{ marginRight: 8, verticalAlign: "middle" }} />
              {i18n.t("messagesAPI.auth.title")}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>{i18n.t("messagesAPI.importantInfo.tokenLabel")}:</strong> {i18n.t("messagesAPI.importantInfo.tokenBody")}</li>
              <li><strong>{i18n.t("messagesAPI.importantInfo.formatLabel")}:</strong> {i18n.t("messagesAPI.importantInfo.formatBody")}</li>
              <li><strong>{i18n.t("messagesAPI.importantInfo.exampleLabel")}:</strong> {i18n.t("messagesAPI.importantInfo.exampleBody")}</li>
            </ul>
          </Box>

          {/* Instancia / Conexión */}
          <Typography id="instance" className={classes.sectionAnchor}>
            <SettingsInputAntennaIcon /> {i18n.t("messagesAPI.nav.instance")}
          </Typography>
          <Alert severity="info" style={{ marginBottom: 12 }}>{i18n.t("messagesAPI.instance.body")}</Alert>

          <EndpointCard
            method="GET"
            path={`${instanceBase}/status`}
            badges={[BAILEYS_BADGE]}
            title={i18n.t("messagesAPI.instance.statusTitle")}
            description={i18n.t("messagesAPI.instance.statusDesc")}
            response={`{ "success": true, "data": { "id": 1, "name": "Ventas", "provider": "baileys", "status": "CONNECTED", "number": "51999888777", "battery": "85", "connected": true } }`}
            curl={`curl -H "Authorization: Bearer <TOKEN>" "${instanceBase}/status"`}
          />

          <EndpointCard
            method="GET"
            path={`${instanceBase}/qr`}
            badges={[BAILEYS_BADGE]}
            title={i18n.t("messagesAPI.instance.qrTitle")}
            description={i18n.t("messagesAPI.instance.qrDesc")}
            response={`{ "success": true, "data": { "status": "qrcode", "connected": false, "qrcode": "2@BNZR...cadena QR...", "qrImage": "data:image/png;base64,iVBORw0KGgo...", "pairingCode": "" } }`}
            curl={`curl -H "Authorization: Bearer <TOKEN>" "${instanceBase}/qr"`}
            note={i18n.t("messagesAPI.instance.qrNote")}
            noteSeverity="info"
          />

          <EndpointCard
            method="GET"
            path={`${instanceBase}/qr/view?token=<TOKEN>`}
            badges={[BAILEYS_BADGE]}
            title={i18n.t("messagesAPI.instance.qrViewTitle")}
            description={i18n.t("messagesAPI.instance.qrViewDesc")}
            curl={`# Abrir en el navegador o incrustar en un iframe:\n${instanceBase}/qr/view?token=<TOKEN>`}
            note={i18n.t("messagesAPI.instance.qrViewNote")}
            noteSeverity="info"
          />

          <EndpointCard
            method="POST"
            path={`${instanceBase}/reconnect`}
            badges={[BAILEYS_BADGE]}
            title={i18n.t("messagesAPI.instance.reconnectTitle")}
            description={i18n.t("messagesAPI.instance.reconnectDesc")}
            response={`{ "success": true, "data": { "message": "Reconexión iniciada. Consulta GET /instance/qr o /instance/status." } }`}
            curl={`curl -X POST "${instanceBase}/reconnect" \\\n  -H "Authorization: Bearer <TOKEN>"`}
          />

          <EndpointCard
            method="POST"
            path={`${instanceBase}/reboot`}
            badges={[BAILEYS_BADGE, { label: "DESTRUCTIVO", color: "#f59e0b" }]}
            title={i18n.t("messagesAPI.instance.rebootTitle")}
            description={i18n.t("messagesAPI.instance.rebootDesc")}
            response={`{ "success": true, "data": { "message": "Reinicio iniciado (logout + nuevo escaneo). Consulta GET /instance/qr." } }`}
            curl={`curl -X POST "${instanceBase}/reboot" \\\n  -H "Authorization: Bearer <TOKEN>"`}
            note={i18n.t("messagesAPI.instance.rebootNote")}
            noteSeverity="warning"
          />

          <EndpointCard
            method="POST"
            path={`${instanceBase}/reset`}
            badges={[BAILEYS_BADGE, { label: "DESTRUCTIVO", color: "#dc2626" }]}
            title={i18n.t("messagesAPI.instance.resetTitle")}
            description={i18n.t("messagesAPI.instance.resetDesc")}
            response={`{ "success": true, "data": { "message": "Reset completo (credenciales borradas). Escanea el nuevo QR (GET /instance/qr)." } }`}
            curl={`curl -X POST "${instanceBase}/reset" \\\n  -H "Authorization: Bearer <TOKEN>"`}
            note={i18n.t("messagesAPI.instance.resetNote")}
            noteSeverity="error"
          />

          <EndpointCard
            method="POST"
            path={`${instanceBase}/webhook`}
            badges={[BAILEYS_BADGE]}
            title={i18n.t("messagesAPI.instance.webhookTitle")}
            description={i18n.t("messagesAPI.instance.webhookDesc")}
            params={[
              { name: "url", value: i18n.t("messagesAPI.instance.webhookUrlParam") },
              { name: "enable", value: i18n.t("messagesAPI.instance.webhookEnableParam") },
            ]}
            request={`{ "url": "https://tu-servidor.com/webhook", "enable": true }`}
            response={`{ "success": true, "data": { "webhook": "https://tu-servidor.com/webhook", "webhookEnabled": true } }`}
            curl={`curl -X POST "${instanceBase}/webhook" \\\n  -H "Authorization: Bearer <TOKEN>" \\\n  -H "Content-Type: application/json" \\\n  -d '{ "url": "https://tu-servidor.com/webhook", "enable": true }'`}
            note={i18n.t("messagesAPI.instance.webhookNote")}
            noteSeverity="info"
          />

          {/* Mensajería directa */}
          <Typography id="messages" className={classes.sectionAnchor}>
            <MessageIcon /> {i18n.t("messagesAPI.nav.messages")}
          </Typography>

          <EndpointCard
            method="POST"
            path={sendEp}
            badges={[BAILEYS_BADGE]}
            title={i18n.t("messagesAPI.section1.title")}
            description={i18n.t("messagesAPI.section1.intro")}
            params={[
              { name: "number", value: "51999888777" },
              { name: "body", value: i18n.t("messagesAPI.reference.textBody") },
            ]}
            request={`{ "number": "51999888777", "body": "Su mensaje" }`}
            response={`{ "mensagem": "Mensaje enviado exitosamente" }`}
            curl={`curl -X POST "${sendEp}" \\\n  -H "Authorization: Bearer <TOKEN>" \\\n  -H "Content-Type: application/json" \\\n  -d '{ "number": "51999888777", "body": "Su mensaje" }'`}
          />

          <EndpointCard
            method="POST"
            path={sendEp}
            badges={[BAILEYS_BADGE, { label: "≤100MB", color: "#27ae60" }]}
            title={i18n.t("messagesAPI.section2.title")}
            description={i18n.t("messagesAPI.section2.intro")}
            params={[
              { name: "number", value: "51999888777" },
              { name: "medias", value: i18n.t("messagesAPI.reference.mediaFile") },
              { name: "body", value: i18n.t("messagesAPI.reference.optionalText") },
            ]}
            request={`Content-Type: multipart/form-data\n\nnumber=51999888777\nbody=Mensaje opcional\nmedias=@archivo.jpg`}
            response={`{ "mensagem": "Mensaje enviado exitosamente" }`}
            curl={`curl -X POST "${sendEp}" \\\n  -H "Authorization: Bearer <TOKEN>" \\\n  -F "number=51999888777" \\\n  -F "body=Mensaje opcional" \\\n  -F "medias=@/ruta/archivo.jpg"`}
            note={`${i18n.t("messagesAPI.section2.images")} JPG/PNG/GIF · ${i18n.t("messagesAPI.section2.videos")} MP4/MOV · ${i18n.t("messagesAPI.section2.documents")} PDF/DOC/XLS · ${i18n.t("messagesAPI.section2.audio")} MP3/OGG`}
            noteSeverity="info"
          />

          {/* Grupos */}
          <Typography id="groups" className={classes.sectionAnchor}>
            <GroupIcon /> {i18n.t("messagesAPI.nav.groups")}
          </Typography>

          <EndpointCard
            method="POST"
            path={groupEp}
            badges={[BAILEYS_BADGE]}
            title={i18n.t("messagesAPI.section3.title")}
            description={i18n.t("messagesAPI.section3.intro")}
            params={[
              { name: "number", value: i18n.t("messagesAPI.reference.groupId") },
              { name: "body", value: i18n.t("messagesAPI.reference.textBody") },
            ]}
            request={`{ "number": "120363123456789012", "body": "Su mensaje" }`}
            response={`{ "success": true, "message": "Mensaje enviado al grupo exitosamente", "groupJid": "...@g.us" }`}
            curl={`curl -X POST "${groupEp}" \\\n  -H "Authorization: Bearer <TOKEN>" \\\n  -H "Content-Type: application/json" \\\n  -d '{ "number": "120363123456789012", "body": "Su mensaje" }'`}
            note={i18n.t("messagesAPI.section3.groupNoteBody")}
          />

          <EndpointCard
            method="POST"
            path={groupEp}
            badges={[BAILEYS_BADGE, { label: "≤100MB", color: "#27ae60" }]}
            title={i18n.t("messagesAPI.section4.title")}
            description={i18n.t("messagesAPI.section4.intro")}
            params={[
              { name: "number", value: i18n.t("messagesAPI.reference.groupId") },
              { name: "medias", value: i18n.t("messagesAPI.reference.mediaFile") },
              { name: "body", value: i18n.t("messagesAPI.reference.optionalText") },
            ]}
            request={`Content-Type: multipart/form-data\n\nnumber=120363123456789012\nbody=Mensaje opcional\nmedias=@archivo.jpg`}
            response={`{ "success": true, "message": "Mensaje enviado al grupo exitosamente", "groupJid": "...@g.us" }`}
            curl={`curl -X POST "${groupEp}" \\\n  -H "Authorization: Bearer <TOKEN>" \\\n  -F "number=120363123456789012" \\\n  -F "body=Mensaje opcional" \\\n  -F "medias=@/ruta/archivo.jpg"`}
            note={i18n.t("messagesAPI.section3.groupNoteBody")}
          />

          {/* CRM / External */}
          <Typography id="external" className={classes.sectionAnchor}>
            <CodeIcon /> {i18n.t("messagesAPI.nav.external")}
          </Typography>
          <ExternalApiReference apiBase={externalApiBase} />

          {/* Cuenta / Provisión (F4) */}
          <Typography id="account" className={classes.sectionAnchor}>
            <VpnKeyIcon /> {i18n.t("messagesAPI.nav.account")}
          </Typography>
          <Alert severity="info" style={{ marginBottom: 12 }}>{i18n.t("messagesAPI.account.body")}</Alert>

          <Box className={classes.authCard}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 8 }}>
              <VpnKeyIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
              {i18n.t("messagesAPI.account.keyTitle")}
            </Typography>
            <CodeBlock>{accountKey || i18n.t("messagesAPI.account.keyLoading")}</CodeBlock>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRegenerateKey}
              style={{ marginTop: 10, textTransform: "none", fontWeight: 700 }}
            >
              {i18n.t("messagesAPI.account.regenerate")}
            </Button>
            <Typography variant="caption" style={{ display: "block", marginTop: 8, color: "#b45309" }}>
              {i18n.t("messagesAPI.account.keyWarning")}
            </Typography>
          </Box>

          <EndpointCard
            method="POST"
            path={instancesEp}
            badges={[ACCOUNT_BADGE]}
            title={i18n.t("messagesAPI.account.createTitle")}
            description={i18n.t("messagesAPI.account.createDesc")}
            params={[
              { name: "name", value: i18n.t("messagesAPI.account.createNameParam") },
              { name: "provider", value: i18n.t("messagesAPI.account.createProviderParam") },
            ]}
            request={`{ "name": "Nueva conexión" }`}
            response={`{ "success": true, "data": { "id": 3, "name": "Nueva conexión", "token": "a1b2c3...", "status": "OPENING", "provider": "baileys" } }`}
            curl={`curl -X POST "${instancesEp}" \\\n  -H "Authorization: Bearer <ACCOUNT_API_KEY>" \\\n  -H "Content-Type: application/json" \\\n  -d '{ "name": "Nueva conexión" }'`}
            note={i18n.t("messagesAPI.account.createNote")}
            noteSeverity="info"
          />

          <EndpointCard
            method="GET"
            path={instancesEp}
            badges={[ACCOUNT_BADGE]}
            title={i18n.t("messagesAPI.account.listTitle")}
            description={i18n.t("messagesAPI.account.listDesc")}
            response={`{ "success": true, "data": [ { "id": 1, "name": "Ventas", "status": "CONNECTED", "number": "51999888777", "provider": "baileys", "token": "a1b2c3...", "connected": true } ] }`}
            curl={`curl -H "Authorization: Bearer <ACCOUNT_API_KEY>" "${instancesEp}"`}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default MessagesAPI;
